import { z } from 'zod'
import type { PaginatedResponse } from '@/shared/api/types'

export const NOTIFICATION_CHANNELS = ['Email', 'SignalR', 'Fcm', 'Sms'] as const

const paginated = <T extends z.ZodTypeAny>(schema: T) =>
  z.object({
    items: z.array(schema),
    totalCount: z.number(),
    pageNumber: z.number(),
    pageSize: z.number(),
    totalPages: z.number(),
  })

export const NotificationListItemSchema = z.object({
  id: z.number(),
  event: z.string(),
  priority: z.string(),
  title: z.string(),
  body: z.string().nullable(),
  payload: z.string().nullable(),
  sourceId: z.number().nullable(),
  createdOn: z.string(),
  readAt: z.string().nullable(),
  acknowledgeableAlertId: z.number().nullable(),
  alertStatus: z.string().nullable(),
})

export const NotificationsInboxResponseSchema = z.object({
  items: paginated(NotificationListItemSchema),
  unreadCount: z.number(),
})

export const AlertListItemSchema = z.object({
  id: z.number(),
  event: z.string(),
  sourceId: z.number(),
  priority: z.string(),
  status: z.string(),
  title: z.string().nullable(),
  payload: z.string().nullable(),
  body: z.string().nullable(),
  escalationLevel: z.number(),
  acknowledgedByUserId: z.number().nullable(),
  acknowledgedAt: z.string().nullable(),
  cancelledByUserId: z.number().nullable(),
  cancelledAt: z.string().nullable(),
  createdOn: z.string(),
})

export const AlertsResponseSchema = z.array(AlertListItemSchema)

export const AudienceTierSchema = z.object({
  permissions: z.array(z.string()),
  departmentIds: z.array(z.number()),
  employeeCategoryIds: z.array(z.number()),
  userIds: z.array(z.number()),
})

export const OrgDefaultSchema = z.object({
  event: z.string(),
  defaultChannels: z.array(z.string()),
  audiencePermissions: z.array(z.string()),
  audienceDepartmentIds: z.array(z.number()),
  audienceEmployeeCategoryIds: z.array(z.number()),
  audienceUserIds: z.array(z.number()),
  isActive: z.boolean(),
  escalationWindowSeconds: z.number().nullable(),
  escalationRenudgeSeconds: z.number().nullable(),
  escalationTiers: z.array(AudienceTierSchema),
})

export const UpsertOrgDefaultSchema = OrgDefaultSchema

export const OrgDefaultsResponseSchema = z.array(OrgDefaultSchema)

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number]
export type NotificationListItem = z.infer<typeof NotificationListItemSchema>
export type NotificationsInboxResponse = {
  items: PaginatedResponse<NotificationListItem>
  unreadCount: number
}
export type AlertListItem = z.infer<typeof AlertListItemSchema>
export type AudienceTier = z.infer<typeof AudienceTierSchema>
export type OrgDefault = z.infer<typeof OrgDefaultSchema>
export type UpsertOrgDefault = z.infer<typeof UpsertOrgDefaultSchema>
